import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import type { Player } from '@/types/player';

interface UserRosterContextType {
  roster: Player[];
  addToRoster: (player: Player) => void;
  removeFromRoster: (playerId: string) => void;
  isPlayerInRoster: (playerId: string) => boolean;
  replaceRoster: (players: Player[]) => void;
  clearRoster: () => void;
  saveRoster: () => Promise<void>;
  loadRoster: () => Promise<void>;
  isLoading: boolean;
}

const UserRosterContext = createContext<UserRosterContextType | undefined>(undefined);

export const useUserRoster = () => {
  const context = useContext(UserRosterContext);
  if (!context) {
    throw new Error('useUserRoster must be used within a UserRosterProvider');
  }
  return context;
};

interface UserRosterProviderProps {
  children: React.ReactNode;
}

export const UserRosterProvider: React.FC<UserRosterProviderProps> = ({ children }) => {
  const { user, isLoaded } = useUser();
  const [roster, setRoster] = useState<Player[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSavedRoster, setLastSavedRoster] = useState<string>('');
  const [hasLoaded, setHasLoaded] = useState(false);

  const addToRoster = (player: Player) => {
    setRoster(prev => {
      if (prev.some(p => p.id === player.id)) return prev;
      return [...prev, player];
    });
  };

  const removeFromRoster = (playerId: string) => {
    setRoster(prev => prev.filter(p => p.id !== playerId));
  };

  const isPlayerInRoster = (playerId: string) => {
    return roster.some(p => p.id === playerId);
  };

  const replaceRoster = (players: Player[]) => {
    setRoster(players);
  };

  const clearRoster = () => {
    setRoster([]);
  };

  // Save roster to user metadata
  const saveRoster = async () => {
    if (!user) return;
    
    // Check if roster has actually changed to prevent unnecessary saves
    const currentRosterString = JSON.stringify(roster.map(p => ({
      id: p.id,
      name: p.name,
      position: p.position,
      team: p.team,
      value: p.value,
      imageUrl: p.imageUrl
    })));
    
    if (currentRosterString === lastSavedRoster) {
      return; // No changes to save
    }
    
    setIsLoading(true);
    try {
      await user.update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          fantasyRoster: roster.map(p => ({
            id: p.id,
            name: p.name,
            position: p.position,
            team: p.team,
            value: p.value,
            imageUrl: p.imageUrl
          }))
        }
      });
      setLastSavedRoster(currentRosterString);
    } catch (error) {
      console.error('Failed to save roster:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load roster from user metadata
  const loadRoster = async () => {
    if (!user || hasLoaded) return;

    setIsLoading(true);
    try {
      const savedRoster = user.unsafeMetadata?.fantasyRoster as any[];
      if (savedRoster && Array.isArray(savedRoster)) {
        setRoster(savedRoster);
        // Update lastSavedRoster to prevent immediate re-save
        setLastSavedRoster(JSON.stringify(savedRoster));
      }
      setHasLoaded(true);
    } catch (error) {
      console.error('Failed to load roster:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load roster when user is available (only once)
  useEffect(() => {
    if (isLoaded && user && !hasLoaded && !isLoading) {
      loadRoster();
    }
  }, [isLoaded, user?.id, hasLoaded, isLoading]);

  // Auto-save roster when it changes (debounced)
  useEffect(() => {
    // Only auto-save if we've loaded data and roster has content and we're not currently loading
    if (!user || !hasLoaded || roster.length === 0 || isLoading) return;
    
    const timeoutId = setTimeout(() => {
      saveRoster();
    }, 2000); // Save 2 seconds after last change

    return () => clearTimeout(timeoutId);
  }, [roster, user?.id, hasLoaded, isLoading]); // Only depend on user ID instead of full user object

  const value: UserRosterContextType = {
    roster,
    addToRoster,
    removeFromRoster,
    isPlayerInRoster,
    replaceRoster,
    clearRoster,
    saveRoster,
    loadRoster,
    isLoading
  };

  return (
    <UserRosterContext.Provider value={value}>
      {children}
    </UserRosterContext.Provider>
  );
}; 